import React, { useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  useTheme,
  alpha,
  Stack,
  LinearProgress,
} from "@mui/material";
import { MdFolder, MdPlayCircle } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectTopPriorityTasks } from "store/features/company/projectManagement/task/taskSelectors";
import { selectCurrentUser } from "store/features/auth/authSelectors";
import { getSecureLocalItem } from "utils/secureSessionStorage";

// Helper to check if the task belongs to the current user
const isMyTask = (task, userId, employeeId, userName) => {
  if (!userId && !employeeId && !userName) return true;

  if (task.assigned_to !== undefined && task.assigned_to !== null) {
    if (Array.isArray(task.assigned_to)) {
      if (
        task.assigned_to.some(
          (id) =>
            String(id) === String(userId) || String(id) === String(employeeId),
        )
      )
        return true;
    } else if (typeof task.assigned_to === "object") {
      if (
        String(task.assigned_to.id) === String(userId) ||
        String(task.assigned_to.id) === String(employeeId) ||
        (task.assigned_to.name &&
          userName &&
          task.assigned_to.name.toLowerCase() === userName.toLowerCase())
      )
        return true;
    } else {
      const assignedStr = String(task.assigned_to);
      if (assignedStr.includes(",")) {
        const ids = assignedStr.split(",").map((s) => s.trim());
        if (
          ids.some(
            (id) =>
              id === String(userId) ||
              id === String(employeeId) ||
              (userName && id.toLowerCase() === userName.toLowerCase()),
          )
        )
          return true;
      } else if (
        assignedStr === String(userId) ||
        assignedStr === String(employeeId) ||
        (userName && assignedStr.toLowerCase() === userName.toLowerCase())
      ) {
        return true;
      }
    }
  }

  if (Array.isArray(task.assignees) && task.assignees.length > 0) {
    if (
      task.assignees.some(
        (a) =>
          String(a.employee_id || a.user_id || a.id) === String(userId) ||
          String(a.employee_id || a.user_id || a.id) === String(employeeId),
      )
    )
      return true;
  }

  if (
    task.created_by &&
    (String(task.created_by) === String(userId) ||
      String(task.created_by) === String(employeeId))
  )
    return true;
  if (
    task.user_id &&
    (String(task.user_id) === String(userId) ||
      String(task.user_id) === String(employeeId))
  )
    return true;

  return false;
};

const ActiveProjectsWidget = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isDark = theme.palette.mode === "dark";
  const tasks = useSelector(selectTopPriorityTasks) || [];
  const currentUser = useSelector(selectCurrentUser);

  const activeProject = useMemo(() => {
    // Get current user info
    const userRaw = getSecureLocalItem("user");
    const storedUser = userRaw
      ? typeof userRaw === "string"
        ? JSON.parse(userRaw)
        : userRaw
      : null;
    const user = storedUser || currentUser;

    const userId = user?.id || user?.user_id;
    const employeeId = user?.employee_id || user?.id;
    const userName =
      user?.name ||
      (user?.first_name
        ? `${user.first_name} ${user.last_name || ""}`.trim()
        : null);

    let list = Array.isArray(tasks) ? tasks : [];

    // Filter out parent tasks
    list = list.filter(
      (t) =>
        t.is_parent_task === 0 ||
        t.is_parent_task === null ||
        t.is_parent_task === undefined,
    );

    // Filter to only my tasks
    list = list.filter((task) => isMyTask(task, userId, employeeId, userName));

    // Find the task I am currently working on
    const inProgressTask = list.find(
      (t) => String(t.status).toLowerCase() === "in_progress",
    );

    if (!inProgressTask) return null;

    // Return the project information attached to this active task
    return {
      name:
        inProgressTask.project_name ||
        inProgressTask.milestone_name ||
        "Active Project",
      code: inProgressTask.project_code || "PROJ-001",
      taskTitle: inProgressTask.title,
      progress: inProgressTask.progress_percentage || 50,
    };
  }, [tasks, currentUser]);

  if (!activeProject) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: "8px",
          bgcolor: isDark ? "#111827" : "#ffffff",
          border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0"}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 100,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          No active project currently.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: "8px",
        bgcolor: isDark ? "#111827" : "#ffffff",
        border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0"}`,
        boxShadow: isDark
          ? "0 1px 3px rgba(0,0,0,0.4)"
          : "0 1px 3px rgba(15,23,42,0.03)",
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1.5,
          pb: 1.5,
          borderBottom: `1px solid ${
            isDark ? "rgba(255,255,255,0.06)" : "#f1f5f9"
          }`,
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: "4px",
              bgcolor: isDark ? alpha("#0ea5e9", 0.2) : "#e0f2fe",
              color: "#0ea5e9",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <MdFolder size={18} />
          </Box>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, fontSize: "1rem" }}
          >
            Active Project
          </Typography>
        </Stack>
        <Typography
          variant="caption"
          onClick={() => navigate("/company/projects")}
          sx={{
            fontWeight: 600,
            color: "#0ea5e9",
            cursor: "pointer",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          View &gt;
        </Typography>
      </Box>

      <Box
        sx={{
          p: 1.5,
          borderRadius: "6px",
          bgcolor: isDark ? "rgba(255,255,255,0.02)" : "#f8fafc",
          border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#f1f5f9"}`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 0.5,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, fontSize: "0.9rem" }}
          >
            {activeProject.name}
          </Typography>
          <Box
            component="span"
            sx={{
              fontSize: "0.65rem",
              fontWeight: 700,
              px: 0.85,
              py: 0.15,
              borderRadius: "4px",
              bgcolor: isDark ? alpha("#10b981", 0.15) : "#d1fae5",
              color: "#059669",
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <MdPlayCircle size={12} /> IN PROGRESS
          </Box>
        </Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mb: 1, fontSize: "0.75rem" }}
        >
          Working on: <strong>{activeProject.taskTitle}</strong>
        </Typography>
        <LinearProgress
          variant="determinate"
          value={activeProject.progress}
          sx={{
            height: 5,
            borderRadius: 2.5,
            bgcolor: isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0",
            "& .MuiLinearProgress-bar": {
              borderRadius: 2.5,
              background: "linear-gradient(90deg, #0ea5e9 0%, #3b82f6 100%)",
            },
          }}
        />
      </Box>
    </Paper>
  );
};

export default ActiveProjectsWidget;
